import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { cn } from '@eon/utils';
import { Skeleton } from './skeleton';
import { EmptyState } from './empty-state';

export type SortDirection = 'asc' | 'desc';

export interface TableSort {
  sortBy: string;
  sortDir: SortDirection;
}

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  /** When true, the column header is clickable for sorting. */
  sortable?: boolean;
  /** API/query sort key; defaults to `key`. */
  sortKey?: string;
  /** Value extractor for client-side sorting. */
  sortValue?: (row: T) => string | number | boolean | null | undefined;
}

export interface DataTableProps<T> {
  data: T[] | undefined;
  columns: Column<T>[];
  loading?: boolean;
  emptyTitle?: ReactNode;
  emptyDescription?: ReactNode;
  rowKey: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  className?: string;
  sort?: TableSort | null;
  onSortChange?: (sort: TableSort | null) => void;
}

function sortColumnKey<T>(column: Column<T>): string {
  return column.sortKey ?? column.key;
}

function nextSort(current: TableSort | null, key: string): TableSort | null {
  if (current?.sortBy !== key) return { sortBy: key, sortDir: 'asc' };
  if (current.sortDir === 'asc') return { sortBy: key, sortDir: 'desc' };
  return null;
}

/**
 * Headless-friendly virtualisation-ready table. For large datasets we plan to
 * swap the body with TanStack virtual; the public surface is stable.
 */
export function DataTable<T>({
  data,
  columns,
  loading,
  emptyTitle = 'No results',
  emptyDescription,
  rowKey,
  onRowClick,
  className,
  sort,
  onSortChange,
}: DataTableProps<T>) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }
  if (!data || data.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }
  return (
    <div className={cn('bg-card relative overflow-hidden rounded-xl border shadow-sm', className)}>
      <div className="scrollbar-thin overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/40 text-muted-foreground border-b text-xs uppercase tracking-wider">
            <tr>
              {columns.map((c) => {
                const colKey = sortColumnKey(c);
                const isSorted = sort?.sortBy === colKey;
                const canSort = c.sortable && onSortChange;
                return (
                  <th key={c.key} className={cn('px-4 py-3 font-medium', c.className)}>
                    {canSort ? (
                      <button
                        type="button"
                        className={cn(
                          'hover:text-foreground inline-flex items-center gap-1 transition-colors',
                          isSorted && 'text-foreground',
                        )}
                        onClick={() => onSortChange(nextSort(sort ?? null, colKey))}
                      >
                        <span>{c.header}</span>
                        {isSorted ? (
                          sort.sortDir === 'asc' ? (
                            <ArrowUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          ) : (
                            <ArrowDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          )
                        ) : (
                          <ArrowUpDown className="h-3.5 w-3.5 shrink-0 opacity-40" aria-hidden />
                        )}
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr
                key={rowKey(row, i)}
                className={cn(
                  'border-b transition-colors last:border-0',
                  onRowClick && 'hover:bg-muted/30 cursor-pointer',
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cn('px-4 py-3 align-middle', c.className)}>
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
